// src/components/Countries/CountriesTable.js
import React from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import Pagination from "../Common/Pagination";
import { 
  MagnifyingGlassIcon, 
  ChevronDownIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from "@heroicons/react/24/outline";

const CountriesTable = ({
  countries,
  onDeleteCountry,
  onEditCountry,
  onAddNew,
  currentPage = 1,
  totalPages = 1,
  total = 0,
  itemsPerPage = 10,
  onPageChange,
  searchValue = "",
  onSearchChange,
  filterValue = "All",
  onFilterChange,
  sortValue = "Name",
  onSortChange
}) => {
  const { t } = useTranslation();
  const startIndex = (currentPage - 1) * itemsPerPage;

  return (
    <div className="bg-white rounded-[10px] border border-dashboardStroke shadow-[0px_9px_15px_rgba(0,0,0,0.07)] overflow-hidden">
      {/* Dark Header */}
      <div className="bg-dashboardDark h-16 px-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-white font-semibold text-lg">{t("countries.title")}</h2>
          <span className="text-white font-semibold">({countries.length})</span>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 border border-white rounded">
            <MagnifyingGlassIcon className="w-[18px] h-[18px] text-white" />
            <input
              type="text"
              placeholder={t("common.search")}
              value={searchValue}
              onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              className="bg-transparent text-white placeholder-white/70 text-sm font-light outline-none w-28"
            />
          </div>
          
          <div className="relative">
            <select
              value={sortValue}
              onChange={(e) => onSortChange && onSortChange(e.target.value)}
              className="appearance-none bg-transparent px-3 py-1.5 pr-8 border border-white rounded text-white text-sm font-light outline-none cursor-pointer"
            >
              <option value="Name" className="text-gray-900">{t("countries.sort.byName")}</option>
              <option value="Code" className="text-gray-900">{t("countries.sort.byCode")}</option>
            </select>
            <ChevronDownIcon className="w-[18px] h-[18px] text-white absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          
          <div className="relative">
            <select
              value={filterValue}
              onChange={(e) => onFilterChange && onFilterChange(e.target.value)}
              className="appearance-none bg-transparent px-3 py-1.5 pr-8 border border-white rounded text-white text-sm font-light outline-none cursor-pointer"
            >
              <option value="All" className="text-gray-900">{t("countries.filter.all")}</option>
              <option value="Active" className="text-gray-900">{t("countries.filter.active")}</option>
              <option value="Closed" className="text-gray-900">{t("countries.filter.inactive")}</option>
            </select>
            <ChevronDownIcon className="w-[18px] h-[18px] text-white absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          
          <button 
            onClick={onAddNew}
            className="flex items-center gap-2 px-4 py-1.5 bg-dashboardCoral rounded text-white text-sm font-medium"
          >
            <PlusIcon className="w-[18px] h-[18px]" />
            <span>{t("common.addNew")}</span>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-dashboardStroke">
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.srNo")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.flag")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.name")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.code")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.currency")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("countries.col.taxRate")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.status")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-medium text-dashboardText">{t("col.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {countries.map((country, index) => (
              <tr 
                key={country._id}
                className={`border-b border-dashboardStroke hover:bg-gray-50 transition-colors ${
                  index === 0 ? "shadow-[inset_3px_0px_0px_#FF7C17]" : ""
                }`}
              >
                <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                  {startIndex + index + 1}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <img
                    src={country.countryFlagImage || "https://via.placeholder.com/40"}
                    alt={country.countryName || "No flag"}
                    className="h-5 w-8 object-cover rounded"
                  />
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                  {country.countryName || "Unknown"}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                  {country.countryCode || "-"}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                  {country.currency || "-"}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                  {country.taxRate ?? "-"}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                    country.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                      country.isActive ? 'bg-green-500' : 'bg-gray-400'
                    }`} />
                    {country.isActive ? t("common.active") : t("common.inactive")}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-0.5">
                    <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                      <EyeIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                    </button>
                    <div className="w-px h-6 bg-dashboardStroke"></div>
                    <button 
                      onClick={() => onEditCountry && onEditCountry(country)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <PencilIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                    </button>
                    <div className="w-px h-6 bg-dashboardStroke"></div>
                    <button 
                      onClick={() => onDeleteCountry && onDeleteCountry(country._id)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <TrashIcon className="w-6 h-6 text-dashboardCoral" strokeWidth={1.3} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {totalPages > 1 && (
        <div className="px-4 py-4 border-t border-dashboardStroke">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            total={total}
            itemsPerPage={itemsPerPage}
          />
        </div>
      )}
    </div>
  );
};

export default CountriesTable;
